import { difference, isEqual } from 'lodash'
import { ActionType, createReducer } from 'typesafe-actions'
import { env, isActiveImage } from 'UTILS'
import { typeFileMap } from '~/constants'
import * as imageAction from '../actions/image'

export type ImageActionType = ActionType<typeof imageAction>

export type ImageType = keyof typeof typeFileMap

export interface Score {
  texture: number
  vqscore: number
  aesthetic: number
}

export interface ImageItem {
  url: string //上传的一些参数，下同
  uid: string
  type: ImageType
  name: string
  width: number
  height: number
  size: number
  template: Template // 每个图片都有自己的template
  files: FileItem[] //处理后的图片
  compareIndex: number //当前对比的图片index
  time?: number
  score?: Score
}

export interface FileItem {
  type: ImageType
  size: number
  url: string
  time?: number
  isFinal?: boolean
  score?: Score
}

export enum CropWay {
  CUSTOM = 0,
  CIRCLE = 1,
  FACE = 2,
  COMIC = 3,
  BORDER = 4,
}

export interface Template {
  mode: 'cut' | 'compare' //当前模板状态，预览？？裁剪？？
  quality: number //一些参数
  sharpen: number
  invert: 0 | 1
  bright: number
  contrast: number
  blur?: number
  saturation: number
  rotate: {
    angle: number
    bgcolor: string
    bgalpha: number
    isClockWise: boolean
  }
  flip: 0 | 1 | 2
  resize: {
    width: number | undefined
    height: number | undefined
    radio: string
  }
  crop: {
    radius: number
    cropWay: CropWay
    width: number
    height: number
    lx: number
    ly: number
  }
  watermark: {
    text: string
    font: string
    size: number
    color: string
    linespace: number
    textOpcity: number
    align: number
    shadow: boolean
    shadowBlur: number
    shadowColor: string
    shadowDist: number
    shadowOpcity: number
    shadowLocation: number
    pic: {
      uid: string
      url: string
      type: string
      time?: number
    }
    picOpcity: number
    widthRadioMin: number
    widthRadioMax: number
    full: boolean
    location: number
    angle: number
    relativeLocation: number
    fullRadio: number
  }
  segment?: {
    mode: 0 | 1
    object_class: 0 | 1 | 2
  }
  enhance?: {
    mode: 0 | 1 | 2
    strategy?: 0 | 1
  }
  super_resolution?: {
    multiple: 2 | 3 | 4
  }
  hiddenmark?: {
    info: string
    strength: 'medium' | 'low' | 'strong'
  }
  vqscore: boolean
}

export interface ImageState {
  imageList: ImageItem[] //图片列表
  active: number //当前选中图片index
  template: Template //初始模板
  applyAll: boolean //应用全部，当图片列表为空时有效
  applyAllActive: boolean //应用全部动图
  applyAllStatic: boolean //应用全部静图
}

export const initTemplate: Template = {
  mode: 'compare',
  quality: 75,
  invert: 0,
  bright: 0,
  contrast: 0,
  sharpen: 0,
  saturation: 0,
  rotate: {
    angle: 0,
    bgalpha: 100,
    bgcolor: '#000000',
    isClockWise: true,
  },
  flip: 0,
  resize: {
    width: undefined,
    height: undefined,
    radio: '1:1',
  },
  watermark: {
    text: '',
    font: '思源黑体',
    size: 30,
    color: '#000000',
    align: 0,
    shadowLocation: 0,
    linespace: 0.25,
    textOpcity: 100,
    shadow: false,
    shadowBlur: 0,
    shadowColor: '#000000',
    shadowDist: 2,
    shadowOpcity: 25,
    pic: {
      uid: '',
      url: '',
      type: 'jpg',
    },
    picOpcity: 100,
    widthRadioMin: 0.35,
    widthRadioMax: 0.9,
    full: false,
    location: 9,
    angle: 0,
    relativeLocation: 0,
    fullRadio: 0.5,
  },
  crop: {
    cropWay: CropWay.CUSTOM,
    lx: 0,
    ly: 0,
    radius: 2048,
    width: 0,
    height: 0,
  },
  vqscore: false,
}

const defaultImageList: ImageItem[] =
  typeof window !== 'undefined' && !sessionStorage.getItem('persist:root')
    ? env.isProd
      ? [
          {
            url:
              'https://p6-imagex-lite.volcimagex.com/imagex-rc/1.jpg~tplv-ivwd9ldvil-image.image',
            uid: 'imagex-rc/1.jpg',
            type: 'jpg',
            name: '1',
            width: 664,
            height: 374,
            size: 175354,
            template: {
              ...initTemplate,
              ...{
                resize: {
                  width: undefined,
                  height: undefined,
                  radio: '664:374',
                },
              },
            },
            files: [],
            compareIndex: 0,
          },
          {
            url:
              'https://p6-imagex-lite.volcimagex.com/imagex-rc/2.jpg~tplv-ivwd9ldvil-image.image',
            uid: 'imagex-rc/2.jpg',
            type: 'jpg',
            name: '2',
            width: 664,
            height: 374,
            size: 142735,
            template: {
              ...initTemplate,
              ...{
                resize: {
                  width: undefined,
                  height: undefined,
                  radio: '664:374',
                },
              },
            },
            files: [],
            compareIndex: 0,
          },
          {
            url:
              'https://p6-imagex-lite.volcimagex.com/imagex-rc/3.jpg~tplv-ivwd9ldvil-image.image',
            uid: 'imagex-rc/3.jpg',
            type: 'jpg',
            name: '3',
            width: 320,
            height: 560,
            size: 148355,
            template: {
              ...initTemplate,
              ...{
                resize: {
                  width: undefined,
                  height: undefined,
                  radio: '320:560',
                },
              },
            },
            files: [],
            compareIndex: 0,
          },
          {
            url:
              'https://p6-imagex-lite.volcimagex.com/imagex-rc/4.jpg~tplv-ivwd9ldvil-image.image',
            uid: 'imagex-rc/4.jpg',
            type: 'jpg',
            name: '4',
            width: 512,
            height: 339,
            size: 158869,
            template: {
              ...initTemplate,
              ...{
                resize: {
                  width: undefined,
                  height: undefined,
                  radio: '512:339',
                },
              },
            },
            files: [],
            compareIndex: 0,
          },
          {
            url:
              'https://p6-imagex-lite.volcimagex.com/imagex-rc/5.jpg~tplv-ivwd9ldvil-image.image',
            uid: 'imagex-rc/5.jpg',
            type: 'jpg',
            name: '5',
            width: 512,
            height: 288,
            size: 174967,
            template: {
              ...initTemplate,
              ...{
                resize: {
                  width: undefined,
                  height: undefined,
                  radio: '512:288',
                },
              },
            },
            files: [],
            compareIndex: 0,
          },
        ]
      : [
          {
            url:
              'https://p-imagex-lite.volcimagex.cn/tos-boe-i-zici86j7y1/1.jpg~tplv-zici86j7y1-image.image',
            uid: 'tos-boe-i-zici86j7y1/1.jpg',
            type: 'jpg',
            name: '1',
            width: 664,
            height: 374,
            size: 175354,
            template: {
              ...initTemplate,
              ...{
                resize: {
                  width: undefined,
                  height: undefined,
                  radio: '664:374',
                },
              },
            },
            files: [],
            compareIndex: 0,
          },
          {
            url:
              'https://p-imagex-lite.volcimagex.cn/tos-boe-i-zici86j7y1/2.jpg~tplv-zici86j7y1-image.image',
            uid: 'tos-boe-i-zici86j7y1/2.jpg',
            type: 'jpg',
            name: '2',
            width: 664,
            height: 374,
            size: 142735,
            template: {
              ...initTemplate,
              ...{
                resize: {
                  width: undefined,
                  height: undefined,
                  radio: '664:374',
                },
              },
            },
            files: [],
            compareIndex: 0,
          },
          {
            url:
              'https://p-imagex-lite.volcimagex.cn/tos-boe-i-zici86j7y1/3.jpg~tplv-zici86j7y1-image.image',
            uid: 'tos-boe-i-zici86j7y1/3.jpg',
            type: 'jpg',
            name: '3',
            width: 320,
            height: 560,
            size: 148355,
            template: {
              ...initTemplate,
              ...{
                resize: {
                  width: undefined,
                  height: undefined,
                  radio: '320:560',
                },
              },
            },
            files: [],
            compareIndex: 0,
          },
          {
            url:
              'https://p-imagex-lite.volcimagex.cn/tos-boe-i-zici86j7y1/4.jpg~tplv-zici86j7y1-image.image',
            uid: 'tos-boe-i-zici86j7y1/4.jpg',
            type: 'jpg',
            name: '4',
            width: 512,
            height: 339,
            size: 158869,
            template: {
              ...initTemplate,
              ...{
                resize: {
                  width: undefined,
                  height: undefined,
                  radio: '512:339',
                },
              },
            },
            files: [],
            compareIndex: 0,
          },
          {
            url:
              'https://p-imagex-lite.volcimagex.cn/tos-boe-i-zici86j7y1/5.jpg~tplv-zici86j7y1-image.image',
            uid: 'tos-boe-i-zici86j7y1/5.jpg',
            type: 'jpg',
            name: '5',
            width: 512,
            height: 288,
            size: 174967,
            template: {
              ...initTemplate,
              ...{
                resize: {
                  width: undefined,
                  height: undefined,
                  radio: '512:288',
                },
              },
            },
            files: [],
            compareIndex: 0,
          },
        ]
    : []

const initialState: ImageState = {
  imageList: defaultImageList,
  active: 0,
  template: initTemplate,
  applyAll: false,
  applyAllActive: false,
  applyAllStatic: false,
}

const reducer = createReducer(initialState)
  //添加图片
  .handleAction(imageAction.addImage, (state, action) => {
    const resize = {
      width: undefined,
      height: undefined,
      radio: `${action.payload.width}:${action.payload.height}`,
    }

    const template = (() => {
      if (state.imageList.length === 0) {
        if (isActiveImage(action.payload.type)) {
          return {
            ...state.template,
            resize,
            segment: undefined,
            enhance: undefined,
            super_resolution: undefined,
            hiddenmark: undefined,
            vqscore: false,
            crop:
              state.template.crop.cropWay === CropWay.CUSTOM ||
              state.template.crop.cropWay === CropWay.CIRCLE
                ? state.template.crop
                : initTemplate.crop,
          }
        } else {
          return {
            ...state.template,
            resize,
          }
        }
      } else {
        return state.template
      }
    })()

    const imageTempte = (() => {
      if (state.imageList.length === 0) {
        return template
      } else {
        //添加的图片是否需要从其他地方复制模板
        const applyAll =
          (state.applyAllActive &&
            isActiveImage(action.payload.type) &&
            state.imageList.findIndex((image) => isActiveImage(image.type))) !== -1 ||
          (state.applyAllStatic &&
            !isActiveImage(action.payload.type) &&
            state.imageList.findIndex((image) => !isActiveImage(image.type)) !== -1)

        //当需要复制时,怎么复制
        const applyAllTemplate = state.imageList.find(
          (image) =>
            (isActiveImage(image.type) && isActiveImage(action.payload.type)) ||
            (!isActiveImage(image.type) && !isActiveImage(action.payload.type)),
        )?.template ?? {
          ...initTemplate,
          resize,
        }
        return applyAll
          ? applyAllTemplate
          : {
              ...initTemplate,
              resize,
            }
      }
    })()

    const imageItem: ImageItem = {
      ...action.payload,
      template: imageTempte,
      files: [],
      compareIndex: 0,
    }
    return {
      ...state,
      ...{ imageList: state.imageList.concat(imageItem) },
      template,
    }
  })
  //删除图片
  .handleAction(imageAction.removeImage, (state, action) => {
    const { payload } = action
    if (state.imageList.length === 1) {
      return {
        ...state,
        ...{
          imageList: state.imageList.filter((_, index) => index !== payload),
        },
        ...{ active: 0 },
      }
    } else {
      return {
        ...state,
        ...{
          imageList: state.imageList.filter((_, index) => index !== payload),
        },
        ...{ active: payload - 1 > 0 ? payload - 1 : 0 },
      }
    }
  })
  //自动删除图片
  .handleAction(imageAction.removeImages, (state, action) => {
    const { payload } = action
    const nextAcitve = payload.includes(state.active)
      ? difference(
          payload,
          state.imageList.map((_, index) => index),
        ).slice(-1)[0] || 0
      : state.active - payload.filter((v) => v < state.active).length
    return {
      ...state,
      ...{
        imageList: state.imageList.filter((_, index) => !payload.includes(index)),
      },
      ...{
        active: nextAcitve,
      },
    }
  })
  //删除所有图片
  .handleAction(imageAction.removeAllImage, (state) => ({
    ...state,
    ...{ imageList: [] },
    active: 0,
  }))
  //改变当前图片列表的选中图
  .handleAction(imageAction.setActiveImage, (state, action) => ({
    ...state,
    ...{ active: action.payload },
    template: state.imageList[action.payload].template,
  }))
  //改变template
  .handleAction(imageAction.changeTemplate, (state, action) => {
    if (state.imageList.length === 0) {
      return {
        ...state,
        template: {
          ...state.template,
          ...action.payload,
        },
      }
    } else {
      return {
        ...state,
        ...{
          imageList: state.imageList.map((item, index) =>
            index === state.active ||
            (state.applyAllActive &&
              isActiveImage(item.type) &&
              isActiveImage(state.imageList[state.active].type)) ||
            (state.applyAllStatic &&
              !isActiveImage(item.type) &&
              !isActiveImage(state.imageList[state.active].type))
              ? {
                  ...item,
                  template: {
                    ...item.template,
                    ...action.payload,
                  },
                }
              : item,
          ),
        },
        template: {
          ...state.template,
          ...action.payload,
        },
      }
    }
  })
  //处理后的图片
  .handleAction(imageAction.addFiles, (state, action) => ({
    ...state,
    ...{
      imageList: state.imageList.map((item, index) =>
        index === action.payload.index
          ? {
              ...item,
              files: action.payload.files,
            }
          : item,
      ),
    },
  }))
  //改变对比图
  .handleAction(imageAction.setCompareIndex, (state, action) => ({
    ...state,
    ...{
      imageList: state.imageList.map((item, index) =>
        index === state.active
          ? {
              ...item,
              compareIndex: action.payload,
            }
          : item,
      ),
    },
  }))
  //重置模板
  .handleAction(imageAction.resetTemplate, (state) => ({
    ...state,
    template:
      state.imageList.length > 0
        ? {
            ...initTemplate,
            ...{
              resize: {
                width: undefined,
                height: undefined,
                radio: `${state.imageList[0].width}:${state.imageList[0].height}`,
              },
            },
          }
        : initTemplate,
    ...{
      active: 0,
      imageList: state.imageList.map((item) => {
        return {
          ...item,
          files: isEqual(item.template, initTemplate) ? item.files : [],
          compareIndex: 0,
          template: {
            ...initTemplate,
            ...{
              resize: {
                width: undefined,
                height: undefined,
                radio: `${item.width}:${item.height}`,
              },
            },
          },
        }
      }),
    },
  }))
  //设置应用全部
  .handleAction(imageAction.setApplyAll, (state) => ({
    ...state,
    applyAll: !state.applyAll,
    applyAllActive: !state.applyAll,
    applyAllStatic: !state.applyAll,
  }))
  //设置应用全部动图
  .handleAction(imageAction.setApplyAllActive, (state) => ({
    ...state,
    applyAllActive: !state.applyAllActive,
    imageList: state.imageList.map((item) => {
      return state.applyAllActive || !isActiveImage(item.type)
        ? item
        : {
            ...item,
            template: state.template,
          }
    }),
  }))
  //设置应用全部静图
  .handleAction(imageAction.setApplyAllStatic, (state) => ({
    ...state,
    applyAllStatic: !state.applyAllStatic,
    imageList: state.imageList.map((item) => {
      return state.applyAllStatic || isActiveImage(item.type)
        ? item
        : {
            ...item,
            template: state.template,
          }
    }),
  }))
  //更新cropSize
  .handleAction(imageAction.updateCropSize, (state, action) => {
    return {
      ...state,
      ...{
        imageList: state.imageList.map((item, index) =>
          index === state.active
            ? {
                ...item,
                template: {
                  ...item.template,
                  crop: {
                    ...item.template.crop,
                    ...action.payload,
                  },
                },
              }
            : item,
        ),
      },
      template: {
        ...state.template,
        crop: {
          ...state.template.crop,
          ...action.payload,
        },
      },
    }
  })
  //切换模板状态
  .handleAction(imageAction.changeTemplageMode, (state, action) => {
    return {
      ...state,
      ...{
        imageList: state.imageList.map((item, index) =>
          index === state.active
            ? {
                ...item,
                template: {
                  ...item.template,
                  ...action.payload,
                },
              }
            : item,
        ),
      },
      template: {
        ...state.template,
        ...action.payload,
      },
    }
  })
  .handleAction(imageAction.updateFile, (state, action) => {
    return {
      ...state,
      ...{
        imageList: state.imageList.map((item, index) =>
          index === state.active
            ? {
                ...item,
                files: item.files.map((i) =>
                  i.url === action.payload.url ? action.payload : i,
                ),
              }
            : item,
        ),
      },
    }
  })
  .handleAction(imageAction.updateImage, (state, action) => {
    return {
      ...state,
      ...{
        imageList: state.imageList.map((item, index) =>
          index === state.active
            ? {
                ...item,
                ...action.payload,
              }
            : item,
        ),
      },
    }
  })
  .handleAction(imageAction.clearAllFiles, (state) => {
    return {
      ...state,
      ...{
        imageList: state.imageList.map((item) => {
          return {
            ...item,
            files: [],
          }
        }),
      },
    }
  })

export default reducer
